"use client"

/**
 * Page create/edit form body (round-5 redesign — matches PostForm).
 *
 * Same principles as PostForm:
 * - antd default Form.Item labels (13/500), no custom `text-2xl` spans
 * - Full-width inside FormShell main column (no hardcoded w-[77%])
 * - FormSections group fields with white-card chrome from round-5
 * - `requiredMark="optional"` — mark only optional fields, keep required
 *   ones visually clean
 * - Media picker modal uses plain string title (relies on antd default
 *   padding now that the global .ant-modal reset is gone)
 */

import { domainOptions } from "@/(admin)/modules/page/pages/List"
import { FormSection } from "@/(admin)/components/form"
import { getLocalizedHref } from "@/lib/locale-utils"
import { toImageSrc } from "@/lib/utils"
import { generateSlug } from "@/utils/remove-accents"
import { CameraOutlined, EditOutlined } from "@ant-design/icons"
import { Button, Form, Input, Modal, Select, Typography } from "antd"
import { t } from "i18next"
import React, { useEffect, useMemo, useRef, useState } from "react"
import ReactQuill from "react-quill"
import { BlockContentNavigator } from "../../../../(admin)/components/block-content-navigator"
import CKEditorWrapper from "../../../../lib/config/lib/CkeditorWrapper"
import { PageFormProps } from "../../../../lib/config/types"
import { slugPattern } from "../../../../utils/validationPatterns"
import Media from "../../media/pages/Media"
import { FieldType, MediaData, PageData } from "../../ui-builder/config/types"
import pageService from "../services/page"

const useBlockContent = (page: PageData | null) => {
  const [isUseBlockContent, setIsUseBlockContent] = useState(false)
  const [blockContentId, setBlockContentId] = useState<any>(null)

  React.useEffect(() => {
    if (page?.attributes?.blockContent?.data || page?.attributes?.isUseUIBuilder) {
      setIsUseBlockContent(true)
      setBlockContentId(page.attributes.blockContent.data?.id)
    }
  }, [page])

  return { isUseBlockContent, blockContentId }
}

const isValidJson = (str: string): boolean => {
  try {
    JSON.parse(str)
  } catch {
    return false
  }
  return true
}

type ParentOption = { value: number; label: string; path: string }

const PageForm: React.FC<PageFormProps> = ({
  page,
  edit,
  form,
  onFinishFailed,
  quillValue,
  onFinish,
  onChangeMetaImage,
  isUIBuilderPage,
  onValuesChange,
  language,
}) => {
  const quillRef = React.useRef<ReactQuill>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMetaImage, setModalMetaImage] = useState(false)
  const [isEditableSlug, setIsEditableSlug] = useState(true)
  const pageServices = pageService()

  // ─── Path model: parent tree → precomputed path, unique per (path, locale) ───
  const domain = Form.useWatch("domain", form) as string | undefined
  const parentId = Form.useWatch("parent", form) as number | null | undefined
  const slugValue = Form.useWatch("slug", form) as string | undefined
  const locale = language || page?.attributes?.locale || "vi"
  const excludeId = page?.id

  const [parentOptions, setParentOptions] = useState<ParentOption[]>([])
  const [pathStatus, setPathStatus] = useState<"idle" | "checking" | "ok" | "taken">("idle")

  // Load selectable parents (same domain + locale). Exclude this page and its
  // whole subtree — picking a descendant as parent would create a cycle.
  useEffect(() => {
    // Parent list is domain-scoped; without a domain there's nothing to filter
    // by (and the Select is disabled), so skip the fetch entirely.
    if (!domain) {
      setParentOptions([])
      return
    }
    let active = true
    void (async () => {
      const rows = await pageServices.fetchParentOptions({ domain, locale, excludeId })
      if (!active) return

      // Map parent → children, then collect all descendants of the current page.
      const childrenOf = new Map<number, number[]>()
      rows.forEach((r: any) => {
        const pid = r?.attributes?.parent?.data?.id
        if (pid != null) childrenOf.set(pid, [...(childrenOf.get(pid) ?? []), r.id])
      })
      const banned = new Set<number>()
      if (excludeId != null) {
        const stack = [excludeId]
        while (stack.length) {
          const cur = stack.pop() as number
          for (const child of childrenOf.get(cur) ?? []) {
            if (!banned.has(child)) {
              banned.add(child)
              stack.push(child)
            }
          }
        }
      }

      setParentOptions(
        rows
          .filter((r: any) => !banned.has(r.id))
          .map((r: any) => {
            const a = r?.attributes ?? {}
            // Label = tên trang (path đầy đủ đã hiển thị ở "Đường dẫn cuối").
            return { value: r.id, label: a.name || a.slug || `#${r.id}`, path: a.path || a.slug || "" }
          })
          .sort((x: ParentOption, y: ParentOption) => x.label.localeCompare(y.label, "vi")),
      )
    })()
    return () => {
      active = false
    }
    // pageServices is recreated each render; intentionally omitted (matches repo pattern).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, locale, excludeId])

  const parentPath = useMemo(
    () => parentOptions.find(o => o.value === parentId)?.path ?? "",
    [parentOptions, parentId],
  )

  // Live preview of the full route this page will occupy.
  const pathPreview = useMemo(() => {
    const seg = generateSlug(slugValue || "")
    if (!seg) return ""
    return parentPath ? `${parentPath}/${seg}` : seg
  }, [parentPath, slugValue])

  // Debounced (path, locale) availability check against the backend.
  useEffect(() => {
    const seg = generateSlug(slugValue || "")
    if (!seg) {
      setPathStatus("idle")
      return
    }
    setPathStatus("checking")
    const handle = setTimeout(async () => {
      const res = await pageServices.checkPath({
        slug: seg,
        parentId: parentId ?? null,
        locale,
        excludeId,
      })
      setPathStatus(res?.available ? "ok" : "taken")
    }, 400)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugValue, parentId, locale, excludeId])

  // Track whether slug is still in auto-generate mode.
  // Starts true (auto). Set to false when:
  //   - page loads in edit mode with an existing slug (from DB)
  //   - user unlocks the slug field for manual editing
  const slugAutoModeRef = useRef(true)

  useEffect(() => {
    if (page?.attributes?.slug) {
      slugAutoModeRef.current = false
    }
  }, [page])

  const handleSubmit = () => {
    const editorContent = quillRef.current?.getEditor().root.innerHTML || form.getFieldValue("content") || ""
    const rawSlug = form.getFieldValue("slug") || ""
    const normalizedSlug = generateSlug(rawSlug)
    form.setFieldsValue({ content: editorContent, slug: normalizedSlug })
    onFinish(form.getFieldsValue())
  }

  useEffect(() => {
    if (quillValue && quillRef.current) {
      const editor = quillRef.current.getEditor()
      editor.root.innerHTML = quillValue
    }
  }, [quillValue])

  const handleOpenModal = (metaImage: boolean) => {
    setModalMetaImage(metaImage)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => setIsModalOpen(false)

  const handleSelectMedia = (media: MediaData) => {
    if (quillRef.current) {
      const quillEditor = quillRef.current.getEditor()
      if (quillEditor) {
        const range = quillEditor.getSelection(true)
        quillEditor.insertEmbed(range.index, "image", toImageSrc(media.url))
      }
    } else {
      const currentContent = form.getFieldValue("content") || ""
      const imageHtml = `<figure class="image"><img src="${toImageSrc(media.url)}" alt="${media.name || ""}" /></figure>`
      form.setFieldsValue({ content: currentContent + imageHtml })
      onValuesChange?.()
    }
    setIsModalOpen(false)
  }

  const handleSelectMetaImage = (media: MediaData) => {
    onChangeMetaImage(media)
    setIsModalOpen(false)
  }

  const handleNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const nameValue = e.target.value.trim()
    if (!nameValue) {
      form.setFields([{ name: "slug", errors: [] }])
      return
    }

    const currentSlug = (form.getFieldValue("slug") || "").trim()
    // Auto-generate when: still in auto mode OR slug field is empty
    if (!slugAutoModeRef.current && currentSlug) return

    const generated = generateSlug(nameValue)
    form.setFieldsValue({ slug: generated })
    form.setFields([{ name: "slug", errors: [] }])

    // Uniqueness is now (path, locale) — surfaced by the live path-check effect,
    // so no slug-only check here (slugs may repeat across pages/locales).
    setTimeout(() => onValuesChange?.(), 0)
  }

  const handleSlugBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const normalized = generateSlug(raw)
    if (normalized !== raw) {
      form.setFieldsValue({ slug: normalized })
      setTimeout(() => onValuesChange?.(), 0)
    }
  }

  const handleToggleSlugEdit = () => {
    const willBeEditable = isEditableSlug // currently locked → will become unlocked
    setIsEditableSlug(!isEditableSlug)
    if (willBeEditable) {
      // User is unlocking the slug field → they take manual control
      slugAutoModeRef.current = false
    }
  }

  // @ts-expect-error
  const { isUseBlockContent, blockContentId } = useBlockContent(page)

  return (
    <Form
      form={form}
      name="page"
      initialValues={{ remember: true }}
      onFinish={handleSubmit}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      layout="vertical"
      onValuesChange={onValuesChange}
      requiredMark="optional"
    >
      {/* ─── Cơ bản ─────────────────────────────────────────── */}
      <FormSection title={t("Cơ bản")} collapsible={false}>
        <Form.Item<FieldType>
          name="name"
          label={t("Tên trang")}
          rules={[{ required: true, message: t("Vui lòng nhập tên trang!") }]}
        >
          <Input
            placeholder={t("VD: Giới thiệu Tamda Group")}
            onBlur={handleNameBlur}
            onChange={e => {
              form.setFieldValue("metaTitle", e.target.value)
              if (form.getFieldValue("excerpt") == undefined) {
                form.setFieldValue("metaDescription", e.target.value)
              }
            }}
          />
        </Form.Item>

        <Form.Item<FieldType>
          name="domain"
          label={t("Tên miền")}
          rules={[{ required: true, message: t("Vui lòng chọn tên miền!") }]}
        >
          <Select placeholder={t("Chọn tên miền")} allowClear>
            {domainOptions.map((domain: { value: string; label: string }) => (
              <Select.Option key={domain.value} value={domain.value}>
                {domain.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item<FieldType>
          name="parent"
          label={t("Trang cha")}
          extra={t("Chọn để tạo cấp con. Bỏ trống nếu là trang gốc. Danh sách theo tên miền + ngôn ngữ.")}
        >
          <Select
            placeholder={domain ? t("Không có (trang gốc)") : t("Chọn tên miền trước")}
            allowClear
            showSearch
            optionFilterProp="label"
            disabled={!domain}
            options={parentOptions}
            notFoundContent={t("Không có trang phù hợp")}
          />
        </Form.Item>

        <Form.Item<FieldType>
          name="slug"
          label={t("Đường dẫn")}
          extra={
            pathPreview ? (
              <span>
                {t("Đường dẫn cuối")}:{" "}
                <Typography.Text code>{getLocalizedHref(locale, pathPreview)}</Typography.Text>{" "}
                {pathStatus === "checking" && (
                  <Typography.Text type="secondary">({t("đang kiểm tra…")})</Typography.Text>
                )}
                {pathStatus === "ok" && <Typography.Text type="success">✓ {t("còn trống")}</Typography.Text>}
                {pathStatus === "taken" && (
                  <Typography.Text type="danger">✕ {t("đã tồn tại cho ngôn ngữ này")}</Typography.Text>
                )}
              </span>
            ) : (
              t("Tự động sinh từ tên. Nối sau đường dẫn của 'Trang cha' (nếu có).")
            )
          }
          rules={[
            {
              pattern: slugPattern,
              message: "Chỉ dùng A-Z, a-z, 0-9, - , _ , . , ~",
            },
            { required: true, message: t("Vui lòng thêm đường dẫn!") },
          ]}
        >
          <Input
            disabled={isEditableSlug}
            placeholder={t("vd-duong-dan-trang")}
            onBlur={handleSlugBlur}
            addonAfter={
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={handleToggleSlugEdit}
              >
                {isEditableSlug ? t("Chỉnh sửa") : t("Khoá")}
              </Button>
            }
          />
        </Form.Item>
      </FormSection>

      {/* ─── Nội dung ──────────────────────────────────────── */}
      {isUseBlockContent || isUIBuilderPage ? (
        <FormSection title={t("Nội dung")} collapsible={false}>
          <BlockContentNavigator blockContentId={blockContentId} edit={!!edit} />
        </FormSection>
      ) : (
        <FormSection title={t("Nội dung")}>
          <div className="mb-3">
            <Button icon={<CameraOutlined />} onClick={() => handleOpenModal(false)}>
              {t("Thêm ảnh / video")}
            </Button>
          </div>
          <Form.Item<FieldType> name="content" className="mb-0">
            <div
              style={{
                height: "500px",
                overflowY: "auto",
                position: "relative",
                border: "1px solid var(--admin-border)",
                borderRadius: 8,
              }}
            >
              <CKEditorWrapper
                value={form.getFieldValue("content")}
                onChange={(data: string) => {
                  form.setFieldsValue({ content: data })
                  onValuesChange?.()
                }}
              />
            </div>
          </Form.Item>
        </FormSection>
      )}

      {/* ─── SEO ─────────────────────────────────────────── */}
      <FormSection title="SEO" defaultOpen={false} description={t("Tuỳ biến khi cần")}>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item<FieldType> name="metaTitle" label={t("Meta title")}>
            <Input placeholder={t("Mặc định lấy từ tên trang")} />
          </Form.Item>

          <Form.Item<FieldType> name="keywords" label={t("Từ khoá")}>
            <Input placeholder={t("Cách nhau bằng dấu phẩy")} />
          </Form.Item>

          <Form.Item<FieldType> name="metaDescription" label={t("Meta description")} className="col-span-2">
            <Input.TextArea rows={3} placeholder={t("Mặc định lấy từ mô tả ngắn")} showCount maxLength={160} />
          </Form.Item>

          <Form.Item<FieldType>
            name="structuredData"
            label={t("Structured data (JSON-LD)")}
            className="col-span-2"
            rules={[
              {
                validator: (_, value) => {
                  if (value && !isValidJson(value)) {
                    return Promise.reject(new Error(t("JSON không hợp lệ")))
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input.TextArea rows={4} placeholder='{"@context": "https://schema.org", ...}' />
          </Form.Item>

          <Form.Item<FieldType> name="metaRobots" label={t("Meta robots")}>
            <Input placeholder="index, follow" />
          </Form.Item>

          <Form.Item<FieldType> name="metaViewport" label={t("Meta viewport")}>
            <Input placeholder="width=device-width" />
          </Form.Item>

          <Form.Item<FieldType> name="canonicalURL" label={t("Canonical URL")} className="col-span-2">
            <Input placeholder="https://..." />
          </Form.Item>
        </div>
      </FormSection>

      <Modal
        open={isModalOpen}
        title={t("Chọn media")}
        onCancel={handleCloseModal}
        style={{ top: 20 }}
        width="95%"
        footer={null}
      >
        <Media
          isOpenModal={true}
          selectMode={modalMetaImage ? "single" : "multi"}
          onSelectMedia={media => {
            modalMetaImage ? handleSelectMetaImage(media as MediaData) : handleSelectMedia(media)
          }}
        />
      </Modal>
    </Form>
  )
}

export default PageForm
